/**
 * BulkValidateModal - Modal pour valider plusieurs transactions en masse
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { CheckCircle, AlertTriangle, Loader2, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BulkValidateModal = ({ isOpen, onClose, onSuccess, project = "WORLD CUP 2026" }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleValidate = async () => {
    if (!text.trim()) {
      toast.error('Veuillez entrer des références de transaction');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      // Extraire les références du texte
      const refs = text
        .split(/[,;\s\n\r]+/)
        .map(r => r.trim().toUpperCase())
        .filter(r => r.length > 0);

      if (refs.length === 0) {
        toast.error('Aucune référence valide trouvée');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/tickets/bulk-validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          references: refs,
          project: project
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success(`${data.validated_count} transaction(s) validée(s)`);
        if (onSuccess) onSuccess();
      } else {
        toast.error(data.message || 'Erreur lors de la validation');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setText('');
    setResult(null);
    onClose();
  };

  const exampleText = `Exemple de format:
REF001, REF002, REF003
ou
REF001
REF002
REF003`;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-zinc-900 border-[#C4A052]/30 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            Validation en masse
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">
              Collez vos références de transaction (une par ligne ou séparées par des virgules)
            </label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={exampleText}
              className="bg-zinc-800 border-zinc-700 text-white min-h-[150px] font-mono text-sm"
              disabled={loading}
            />
            <p className="text-xs text-zinc-500 mt-1">
              {text.split(/[,;\s\n\r]+/).filter(r => r.trim()).length} référence(s) détectée(s)
            </p>
          </div>

          {/* Résultats */}
          {result && (
            <div className="bg-zinc-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="h-4 w-4" />
                <span className="font-medium">{result.validated_count} transaction(s) validée(s)</span>
              </div>
              
              <div className="text-sm text-zinc-400 space-y-1">
                <p>• {result.vente_updated} VENTE mise(s) à jour</p>
                <p>• {result.achat_updated} ACHAT mis à jour</p>
              </div>

              {result.not_found_count > 0 && (
                <div className="text-sm text-yellow-400 flex items-start gap-2 mt-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p>{result.not_found_count} référence(s) non trouvée(s)</p>
                    {result.not_found.length > 0 && (
                      <p className="text-xs text-zinc-500 mt-1">
                        {result.not_found.slice(0, 5).join(', ')}
                        {result.not_found.length > 5 && ` ...et ${result.not_found.length - 5} autres`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {result.already_validated_count > 0 && (
                <div className="text-sm text-blue-400">
                  <p>{result.already_validated_count} déjà validée(s)</p>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-zinc-700"
          >
            <X className="h-4 w-4 mr-2" />
            Fermer
          </Button>
          <Button
            onClick={handleValidate}
            disabled={loading || !text.trim()}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Validation...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Valider les transactions
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkValidateModal;
